import React, { useState } from 'react';
import { Menu, X, Sparkles, Monitor, Lock } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/Button';

export const Navbar = ({ onOpenQuote }: { onOpenQuote: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Corporate', path: '/corporate' },
    { name: 'Hardware & Venues', path: '/product/permanent-install', icon: <Monitor size={14} /> },
    { name: 'Locations', path: '/locations' },
    { name: 'Case Studies', path: '/#case-studies', isHash: true },
    { name: 'Event Wizard', path: '/event-wizard', special: true },
  ];

  const handleNavClick = (path: string, isHash?: boolean) => {
    setIsOpen(false);
    if (isHash) {
      if (location.pathname !== '/') {
        navigate('/');
        // Use a small timeout to allow navigation to complete before scrolling
        setTimeout(() => {
          const element = document.getElementById(path.substring(2)); // remove /#
          element?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const element = document.getElementById(path.substring(2));
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <nav className="fixed w-full z-40 top-0 left-0 bg-slate-950/80 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-neonBlue to-brand-neonPink flex items-center justify-center text-white font-bold text-xl shadow-[0_0_15px_rgba(34,211,238,0.5)] group-hover:shadow-[0_0_25px_rgba(217,70,239,0.5)] transition-all">
              Q
            </div>
            <span className="font-display font-bold text-xl tracking-wider text-white">
              QUIK<span className="text-brand-neonBlue">PIX</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-8">
              {navLinks.map((link) => (
                <React.Fragment key={link.name}>
                  {link.isHash ? (
                    <button
                      onClick={() => handleNavClick(link.path, true)}
                      className="px-3 py-2 rounded-md text-sm font-medium transition-colors text-slate-300 hover:text-white"
                    >
                      {link.name}
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                        location.pathname === link.path 
                          ? 'text-brand-neonBlue' 
                          : link.special ? 'text-brand-neonPink hover:text-white' : 'text-slate-300 hover:text-white'
                      }`}
                    >
                      {link.special && <Sparkles size={14} />}
                      {link.icon}
                      {link.name}
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* CTA Button & Login */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login" className="text-slate-500 hover:text-white transition-colors p-2" title="Licensee Login">
                <Lock size={20} />
            </Link>
            <Button size="sm" onClick={onOpenQuote}>Get A Quote</Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-slate-300 hover:text-white p-2"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-slate-900 border-b border-white/10">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <React.Fragment key={link.name}>
                 {link.isHash ? (
                    <button
                      onClick={() => handleNavClick(link.path, true)}
                      className="block w-full text-left px-3 py-4 text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800 rounded-md"
                    >
                      {link.name}
                    </button>
                 ) : (
                    <Link
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`block px-3 py-4 text-base font-medium rounded-md flex items-center gap-2 ${
                        link.special ? 'text-brand-neonPink' : 'text-slate-300'
                      } hover:text-white hover:bg-slate-800`}
                    >
                      {link.special && <Sparkles size={16} />}
                      {link.icon}
                      {link.name}
                    </Link>
                 )}
              </React.Fragment>
            ))}
            <Link to="/partners" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800 rounded-md">
              Agency Partners
            </Link>
            <Link to="/login" onClick={() => setIsOpen(false)} className="block px-3 py-4 text-base font-medium text-slate-500 hover:text-white hover:bg-slate-800 rounded-md flex items-center gap-2">
              <Lock size={16} /> Licensee Portal
            </Link>
            <div className="pt-4">
              <Button className="w-full" onClick={() => { setIsOpen(false); onOpenQuote(); }}>
                Get A Quote
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
